import dayjs from 'dayjs'
import { ChevronDown, CircleAlert, CircleCheck, Users } from '@/components/icons'
import { toast } from 'sonner'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { hapticLight } from '@/lib/haptics'
import { useTripLegSetPaxBoarded } from '@/lib/hooks'

interface PaxOnboardingCardProps {
  tripLegId: string
  boarded: boolean
  /** When PAX were marked boarded (TripLeg.paxBoardedAt) — shown in the subtitle. */
  boardedAt?: string | Date | null
  paxCount: number
}

/**
 * Flight-level PAX onboarding status card (Figma V1.4). Shows a Pending/Boarded
 * pill that opens a dropdown to switch states. Deliberately has no readOnly
 * prop: crew mark boarding from the otherwise read-only schedule leg drawer,
 * so the pill stays interactive for every company member.
 */
export function PaxOnboardingCard({ tripLegId, boarded, boardedAt, paxCount }: PaxOnboardingCardProps) {
  const { mutate: setPaxBoarded, isPending } = useTripLegSetPaxBoarded()

  const handleSelect = (target: boolean) => {
    hapticLight()
    if (target === boarded) return
    setPaxBoarded(
      { tripLegId, boarded: target },
      {
        onSuccess: () =>
          toast.success(target ? 'PAX marked as boarded' : 'PAX boarding set to pending'),
        onError: () => toast.error('Failed to update PAX status'),
      },
    )
  }

  return (
    <div className="bg-surface-L2 border-border-L2 border flex items-center gap-3 p-3 rounded-2xl">
      <Users className="size-8 text-content-brand-bold shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-content-primary-bold leading-5">
          {boarded ? 'PAX Boarded' : 'PAX Onboarding'}
        </p>
        <p className="text-xs font-medium text-content-primary-subtle tracking-[0.18px]">
          {boarded
            ? boardedAt
              ? `Boarded ${dayjs.utc(boardedAt).format('HH:mm')} UTC`
              : 'PAX have boarded.'
            : 'Mark when PAX have boarded.'}
        </p>
      </div>
      {paxCount === 0 ? (
        <span className="inline-flex h-8 shrink-0 items-center rounded-full bg-content-primary-subtle-light px-3 text-xs font-semibold text-content-primary-subtle">
          No PAX
        </span>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger
            disabled={isPending}
            onPointerDown={() => hapticLight()}
            className={cn(
              'inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full px-3 text-xs font-semibold outline-none disabled:opacity-70 [&>svg]:size-3 [&>svg_path]:[stroke-width:2.5]',
              boarded
                ? 'bg-background-positive-subtle text-background-positive-bold'
                : 'border border-background-notice-bold/10 bg-background-notice-subtle text-background-notice-bold',
            )}
          >
            {boarded ? <CircleCheck /> : <CircleAlert />}
            {boarded ? 'Boarded' : 'Pending'}
            <ChevronDown />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[141px] rounded-lg p-0.5">
            <DropdownMenuItem
              className="min-h-8 px-2 text-sm font-semibold text-content-primary-bold focus:text-content-primary-bold"
              onClick={() => handleSelect(false)}
            >
              <CircleAlert className="size-4 text-content-primary-subtle" />
              Pending
            </DropdownMenuItem>
            <DropdownMenuItem
              className="min-h-8 px-2 text-sm font-semibold text-content-primary-bold focus:text-content-primary-bold"
              onClick={() => handleSelect(true)}
            >
              <CircleCheck className="size-4 text-content-primary-subtle" />
              Boarded
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}
